import { dlog } from "../observability/debugLogger";
import {
  addConnectivityListener,
  getConnectivitySnapshot,
  removeConnectivityListener,
  setConnectivityVerdict,
} from "./connectivityVerdict";

// 接続状態をどう決めるか (2026-08-23)。ブラウザに触るのはこのファイルだけで、
// 判定の値そのものは connectivityVerdict.ts が持つ (分けてある理由はあちらの冒頭)。
// React 配線は connectivity.ts。
//
// navigator.onLine === false は「ネットワーク接続が無い」という強い主張だが、
// 実際に見ているのは「インターネットに到達できるか」ではない。VPN の切り替え・
// スリープ復帰・ゲートウェイが死んだ Wi-Fi で false のまま固着することが知られており、
// 2026-08-23 の実機では**繋がっているのにオフラインバナーが出た**。
// しかも固着した場合ブラウザは online を撃たない (ブラウザから見れば状態は変わって
// いない) ので、イベントだけを更新経路にすると**直る契機が原理的に存在しない**。
//
// そこで false を「オフラインだ」ではなく「オフラインかもしれない」として扱い、
// バナーを塗る前に自オリジンへ 1 往復して反証を試みる。これは元の仕様
// (docs/superpowers/specs/2026-06-21-ui-design.md の障害表「navigator.onLine + fetch fail」)
// が最初から要求していた形で、実装されたのは前半だけだった。
//
// 偽陰性 (本当にオフラインなのに何も出ない) を作らないための不変条件。
// どれか 1 つでも落とすと「今日より悪い」状態になる:
//   I1. バナーを抑止するのは、自オリジンへの fetch が例外を投げずに settle した後だけ
//   I2. checking から出る経路はすべて有界 (成功 / 拒否 / タイムアウト → abort)
//   I3. navigator.onLine !== false のときは往復を撃たない (今日と挙動が同一)
//   I4. 古い往復の結果が新しい判定を上書きできない (世代ガード)
//   I5. 拒否・タイムアウト・abort・例外はすべてバナーを出す側に落ちる (fail-closed)
//
// 残余リスク (この層では閉じない): 往復が証明するのは「自オリジンに届く」ことで
// あって「インターネット全体に届く」ことではない。Cloudflare には届くが OpenAI /
// ElevenLabs には届かない状況ではバナーを抑止する。ただしその状況では
// navigator.onLine は true なので**今日もバナーは出ていない** (回帰ではない)。
// 埋めるなら app.error{reason:"network"} 起点の別機構が要る
// (docs/tickets/2026-08-23-seed-subscribe-initial-state-unread.md)。

/**
 * 叩き先は自オリジンの静的アセット。
 * - 100 バイトと小さい
 * - functions/_middleware.ts のレート制限は `/api/` だけなので自分で 429 を踏まない
 * - 外部課金ゼロ
 * - Service Worker は存在しないので横取りされない
 */
export const PROBE_URL = "/version.json";

/** ハングした往復の最後の網。これが無いと checking から出られない (I2)。 */
export const PROBE_TIMEOUT_MS = 3000;

/** 追い越しガード (I4)。往復の結果は「自分が最新世代のときだけ」反映する。 */
let generation = 0;
let inFlight: AbortController | null = null;

export { getConnectivitySnapshot };

function abortProbe(): void {
  inFlight?.abort();
  inFlight = null;
}

/** 自オリジンへ 1 往復して、navigator.onLine の主張を確かめる。 */
function startProbe(): void {
  if (inFlight) return;
  const gen = ++generation;
  // 往復中はバナーを塗らない。数百 ms 出て消えるバナーも「壊れている」と読まれる。
  //
  // **ただし既に offline と確定しているときは checking へ落とさない** (実装後レビュー
  // MINOR-3)。落とすと `useConnectivity()` が offline→true→offline と往復し、
  // (1) バナーが一瞬消えて戻る (2) `App.tsx` の `if (!online) setOfflineDismissed(false)`
  // が発火して**× で閉じたバナーが復活する**。オフラインのまま何度もタブを往復する人
  // (= 繋ぎ直しに行っている人) がいちばん踏む。**offline のまま黙って確かめ直す。**
  if (getConnectivitySnapshot() !== "offline") setConnectivityVerdict("checking");

  const controller = new AbortController();
  inFlight = controller;
  // AbortSignal.timeout ではなく AbortController + setTimeout。
  // fake timers で発火させられる形にしておく (テストで I2 を固定するため)。
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  const startedAt = Date.now();

  // fetch は素の自由関数として呼ぶ。window.fetch をメソッド参照で渡すと
  // this=Window 依存で Illegal invocation になる (SessionUploader で R2 保存が
  // 全滅した実例がある)。
  //
  // res.ok は見ない。判定は「バイトが往復したか」だけである:
  //   (a) versionPlugin は apply:"build" なので pnpm dev に /version.json は無い
  //   (b) Cloudflare Pages は未知パスに index.html を HTTP 200 で返す
  //   (c) 5xx が返ってもバイトは往復している
  // ここに res.ok を足すと dev と旧ビルドが「オフライン」になる。
  //
  // 必ず catch する。未処理 rejection にすると crashReporter が app.crash にし、
  // それは CRITICAL_TYPES かつ 5 件/session 上限なので本編に入る前に上限を食う。
  fetch(PROBE_URL, { cache: "no-store", signal: controller.signal })
    .then(() => {
      if (gen !== generation) return; // I4
      dlog("connectivity probe: reachable", { ms: Date.now() - startedAt });
      setConnectivityVerdict("online");
    })
    .catch(() => {
      // 拒否・タイムアウト由来の abort・例外はすべてここ (I5)。
      if (gen !== generation) return; // I4
      dlog("connectivity probe: unreachable", { ms: Date.now() - startedAt });
      setConnectivityVerdict("offline");
    })
    .finally(() => {
      clearTimeout(timer);
      if (inFlight === controller) inFlight = null;
    });
}

/**
 * ブラウザの主張を読み、疑わしいときだけ確かめにいく。
 * onLine が false でなければ往復を撃たない (I3) ので、
 * ブラウザが「オンライン」と言う全員にとって挙動は今日とバイト単位で同一。
 */
function evaluate(): void {
  if (typeof navigator === "undefined" || navigator.onLine !== false) {
    setConnectivityVerdict("online");
    return;
  }
  startProbe();
}

function handleOnline(): void {
  // 「繋がった」は信じてよい (誤って抑止する側に倒れない)。
  // 世代を進めて、飛んでいる古い往復の結果で offline に戻されるのを防ぐ (I4)。
  generation += 1;
  abortProbe();
  setConnectivityVerdict("online");
}

function handleOffline(): void {
  // 「切れた」は疑って確かめる。イベント自体が誤りのことがある。
  startProbe();
}

function handleVisibilityChange(): void {
  // タブへ戻ったときの再判定の契機。これが無いと往復が「新しい一発読み」に
  // なってしまい、1 回外したら結局そのまま固着する。
  //
  // **`checking` のときだけ撃たない。** 実装後レビュー (MAJOR-2) で
  // 「`offline` のときだけ」に絞った形が偽陰性を作ると実測されたため広げた —
  // **`onLine` が false で固着した端末は、本当に切断されても `offline` イベントが
  // 来ない** (ブラウザから見れば状態が変わっていない)。**この修正が救う当の端末で、
  // online → offline を検出する経路が 1 つも存在しなくなっていた。**
  //
  // 代償はタブ切替ごとの 1 往復 (数十バイト・`cache: "no-store"`)。**払わない形にすると、
  // 誤検知を直した端末が今度は「切れたのに気づかない」側で外す。**
  if (document.visibilityState !== "visible") return;
  if (getConnectivitySnapshot() === "checking") return; // 往復中に重ねて撃たない
  startProbe();
}

/**
 * useSyncExternalStore 用の購読。
 *
 * **リスナーを張った「後」に evaluate() を呼ぶのが不変条件**。逆にすると
 * render と effect の隙間に来た online を取り逃す (singleUtteranceRecognizer が
 * addEventListener("abort") の前に signal.aborted を見るのと同じ形)。
 *
 * dev の StrictMode では二重 mount で subscribe → unsubscribe → subscribe となり、
 * onLine が false のとき往復が 2 回撃たれる (production ビルドでは起きない)。
 */
export function subscribeConnectivity(callback: () => void): () => void {
  if (addConnectivityListener(callback) === 1) {
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    evaluate();
  }
  return () => {
    if (removeConnectivityListener(callback) > 0) return;
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    generation += 1; // 飛んでいる往復の結果を無効化してから捨てる
    abortProbe();
    setConnectivityVerdict("checking"); // 購読者は 0 なので通知先は無い
  };
}
